import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { openAuthPopup, AuthPopupOptions } from '../utils/authPopup';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { 
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.95);
  }
  to { 
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(4px);
  z-index: 10000;
  animation: ${fadeIn} 0.2s ease-out;
`;

const Modal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: linear-gradient(145deg, #1a1a2e 0%, #16213e 100%);
  border-radius: 16px;
  padding: 28px;
  max-width: 380px;
  width: 90%;
  z-index: 10001;
  animation: ${slideUp} 0.3s ease-out;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: transparent;
  border: none;
  color: #888;
  font-size: 24px;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
  }
`;

const Title = styled.h2`
  color: #fff;
  font-size: 1.4rem;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Subtitle = styled.p`
  color: #aaa;
  font-size: 0.9rem;
  margin: 0 0 20px 0;
  line-height: 1.4;
`;

const PermissionList = styled.div`
  background: rgba(0, 0, 0, 0.3);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
`;

const PermissionItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 12px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const PermissionIcon = styled.span`
  font-size: 1.1rem;
  flex-shrink: 0;
`;

const PermissionText = styled.div`
  flex: 1;
`;

const PermissionTitle = styled.div`
  color: #fff;
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 2px;
`;

const PermissionDesc = styled.div`
  color: #888;
  font-size: 0.75rem;
  line-height: 1.3;
`;

const Guarantee = styled.div`
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(76, 175, 80, 0.05) 100%);
  border: 1px solid rgba(76, 175, 80, 0.3);
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const GuaranteeIcon = styled.span`
  font-size: 1.3rem;
`;

const GuaranteeText = styled.div`
  color: #81c784;
  font-size: 0.8rem;
  font-weight: 500;
  line-height: 1.4;
`;

const ContinueButton = styled.button`
  width: 100%;
  padding: 14px 20px;
  background: linear-gradient(135deg, #1DA1F2 0%, #0d8bd9 100%);
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.2s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(29, 161, 242, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg {
    width: 20px;
    height: 20px;
    fill: currentColor;
  }
`;

const CancelText = styled.button`
  background: transparent;
  border: none;
  color: #666;
  font-size: 0.8rem;
  cursor: pointer;
  margin-top: 12px;
  width: 100%;
  text-align: center;
  
  &:hover {
    color: #888;
    text-decoration: underline;
  }
`;

interface PreAuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onContinue?: () => void;
  authOptions?: AuthPopupOptions;
  usePopup?: boolean;
}

export const PreAuthModal: React.FC<PreAuthModalProps> = ({
  isOpen,
  onClose,
  onContinue,
  authOptions = {},
  usePopup = true
}) => {
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) return null;

  const handleContinue = () => {
    setIsLoading(true);
    
    if (onContinue) {
      onContinue();
      return;
    }
    
    if (usePopup) {
      openAuthPopup({
        ...authOptions,
        onSuccess: (token) => {
          console.log('Auth success, token received');
          setIsLoading(false);
          onClose();
          // Store token
          if (token) {
            localStorage.setItem('authToken', token);
            localStorage.setItem('jwt_token', token);
          }
          // If there's a redirect in options, go there
          if (authOptions.redirect && authOptions.redirect !== 'auto') {
            window.location.href = `/${authOptions.redirect}`;
          } else {
            window.location.reload();
          }
        },
        onError: (error) => {
          setIsLoading(false);
          
          // Check if auth actually succeeded (token was stored)
          const token = localStorage.getItem('authToken');
          if (token) {
            try {
              const payload = JSON.parse(atob(token.split('.')[1]));
              // Token is valid and not expired - auth succeeded!
              if (payload.exp * 1000 > Date.now()) {
                console.log('Auth succeeded (token found after popup close)');
                onClose();
                window.location.reload();
                return;
              }
            } catch (e) {
              // Invalid token, continue with error handling
            }
          }
          
          // Only log real errors, not popup closed
          if (error !== 'popup_closed') {
            console.error('Auth error:', error);
          }
        }
      });
    } else {
      // Full redirect flow
      const params = new URLSearchParams();
      if (authOptions.redirect) params.set('redirect', authOptions.redirect);
      if (authOptions.testflow) params.set('testflow', 'true');
      window.location.href = `/api/auth/x/login?${params.toString()}`;
    }
  };

  return (
    <>
      <Overlay onClick={onClose} />
      <Modal onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>&times;</CloseButton>
        
        <Title>
          🔐 Sign in with X
        </Title>
        
        <Subtitle>
          You'll be redirected to the official X login page. We've limited our permission requests to the technical minimum required.
        </Subtitle>
        
        <PermissionList>
          <PermissionItem>
            <PermissionIcon>🎯</PermissionIcon>
            <PermissionText>
              <PermissionTitle>Your @handle</PermissionTitle>
              <PermissionDesc>To identify your contest entry and predictions</PermissionDesc>
            </PermissionText>
          </PermissionItem>
          
          <PermissionItem>
            <PermissionIcon>👤</PermissionIcon>
            <PermissionText>
              <PermissionTitle>Profile picture</PermissionTitle>
              <PermissionDesc>To display your avatar next to your predictions</PermissionDesc>
            </PermissionText>
          </PermissionItem>
        </PermissionList>
        
        <Guarantee>
          <GuaranteeIcon>🛡️</GuaranteeIcon>
          <GuaranteeText>
            We <strong>do not</strong> access your followers, who you muted, who you blocked, or your DMs. We cannot post, like, or retweet on your behalf.
          </GuaranteeText>
        </Guarantee>
        
        <PermissionDesc style={{ textAlign: 'center', marginTop: '12px', fontSize: '12px', opacity: 0.7 }}>
          Want to analyze your Bitcoin tweets? Our paid dashboard lets you download and review all your posts.
        </PermissionDesc>
        
        <ContinueButton onClick={handleContinue} disabled={isLoading}>
          {isLoading ? (
            <>Processing...</>
          ) : (
            <>
              <svg viewBox="0 0 24 24">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
              Continue to X
            </>
          )}
        </ContinueButton>
        
        <CancelText onClick={onClose}>
          Cancel
        </CancelText>
      </Modal>
    </>
  );
};

export default PreAuthModal;
